import { Box, Button, Section, Text, useToggle } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import BoxList from 'modules/core/components/BoxList'
import Loading from 'modules/core/components/Loading'
import Popover from 'modules/core/components/Popover'
import PopoverContent from 'modules/core/components/Popover/Content'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import users from 'modules/core/store/collections/users'

import { useFormAccessGroup } from 'modules/surveys/hooks/useAccessGroup'
import formAccessGroupMemberships from 'modules/surveys/store/collections/formAccessGroupMemberships'
import type SurveyForm from 'modules/surveys/store/models/Form'
import type FormAccessGroupMembership from 'modules/surveys/store/models/FormAccessGroupMembership'

import EmployeeLabelSupervisor from '../../Participants/common/EmployeeLabelSupervisor'
import DeleteAuthorAsSupervisorModal from './DeleteAuthorAsSupervisorModal'

interface ItemProps {
  membership: FormAccessGroupMembership
  isReview: boolean
  formId: number
  authorId: number
}

function Item({ membership, isReview, formId, authorId }: ItemProps) {
  const [open, toggle] = useToggle()
  const [deleteModalOpen, toggleDeleteModal] = useToggle(false)
  const user = users.find({
    id: membership.attributes.access_id,
  })
  if (!user) return null

  const userIsAuthor = user.id === authorId
  const {
    access: { id: userId },
  } = useCurrentSession()
  const currentUserIsAuthor = userIsAuthor && userId === authorId

  const title = isReview
    ? i18n.t('surveys.form.participants.table.body.edit.delete.review')
    : i18n.t('surveys.form.participants.table.body.edit.delete.survey')

  const options = (membership: FormAccessGroupMembership) => {
    return [
      {
        title: title,
        type: 'wrong',
        onClick: userIsAuthor
          ? toggleDeleteModal(true)
          : () => asyncAction(() => membership.destroy({ data: { form_id: formId } })),
      } as const,
    ]
  }

  return (
    <Box
      key={user.attributes.id}
      alignItems="center"
      flexDirection="row"
      justifyContent="spaceBetween"
      paddingX="s24"
      paddingY="s32"
      width="full"
      flexGrow
    >
      <EmployeeLabelSupervisor user={user} />
      <Box>
        {!currentUserIsAuthor && (
          <Popover
            attachment="top right"
            targetAttachment="bottom center"
            toggle={toggle}
            open={open}
          >
            <Button onClick={toggle(true)} type="secondary" iconName="ellipsis" />
            <PopoverContent onClose={toggle(false)} options={options(membership)} />
          </Popover>
        )}
        {deleteModalOpen && (
          <DeleteAuthorAsSupervisorModal
            membership={membership}
            onClose={toggleDeleteModal(false)}
          />
        )}
      </Box>
    </Box>
  )
}

interface ItemsProps {
  memberships: FormAccessGroupMembership[]
  isReview: boolean
  formId: number
  authorId: number
}

const Items = observer(({ memberships, isReview, formId, authorId }: ItemsProps) => {
  if (!memberships.length) {
    return (
      <Box aria-label="List of editors">
        <BoxList>
          <Text.Normal>{i18n.t('surveys.form.managers.blank_slate')}</Text.Normal>
        </BoxList>
      </Box>
    )
  }

  return (
    <Box aria-label="List of editors">
      <BoxList>
        {memberships.map((membership) => (
          <Item
            isReview={isReview}
            key={membership.attributes.id}
            membership={membership}
            formId={formId}
            authorId={authorId}
          />
        ))}
      </BoxList>
    </Box>
  )
})

interface Props {
  form: SurveyForm
}

function Supervisors({ form }: Props) {
  const { navigation } = useNavigation()

  const { loading, accessGroup } = useFormAccessGroup(form)
  const isReview = form.isReview
  const authorId = form.attributes.access_id

  // TODO: this code is unreachable it's only used in SurveyConfiguration
  // and SurveyConfiguration is only used in ConfigurationPage if survey_v2 is false
  // survey_v2 will always be truth, I will add a task to remove the survey_v1 legacy code
  const route = isReview
    ? undefined
    : navigation.engagement.surveys.id.configuration.addManager.fullPath({ id: form.attributes.id })

  const addSupervisor = {
    to: route,
    label: i18n.t('surveys.form.managers.add_manager'),
  }

  return (
    <Section
      illustration="people-add"
      title={i18n.t('surveys.form.managers.title')}
      description={i18n.t('surveys.form.managers.description')}
      action={addSupervisor}
    >
      {loading && <Loading />}
      {!loading && accessGroup && (
        <Items
          isReview={isReview}
          memberships={formAccessGroupMemberships.toArray()}
          formId={form.attributes.id}
          authorId={authorId}
        />
      )}
    </Section>
  )
}

export default observer(Supervisors)
